import { makeAuthenticatedRequest } from "./marketOverviewApi";
import { getApiUrl } from "../utils/envConfig";
import type { components } from "./generated/schema";

// Request contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts — CI fails the build if schema.ts is stale. The endpoints'
// 200 responses are untyped in the schema (inline object), so returns are
// annotated as Record<string, unknown> rather than a fabricated type.
type UserPreferencesBody = components["schemas"]["UserPreferencesBody"];
type FavoriteWorkflowsBody = components["schemas"]["FavoriteWorkflowsBody"];
type SwitcherVisibilityBody =
  components["schemas"]["SwitcherVisibilityBody"];
export type PersonaConfigBody = components["schemas"]["PersonaConfigBody"];

export const fetchUserPreferences = async (): Promise<
  Record<string, unknown>
> => {
  const url = `${getApiUrl()}/user_preferences`;
  return makeAuthenticatedRequest(url);
};

export const saveUserPreferences = async ({
  id_client,
  table_id,
  preferences,
}: UserPreferencesBody & {
  id_client: string;
  table_id: string;
}): Promise<Record<string, unknown>> => {
  const url = `${getApiUrl()}/user_preferences/${id_client}/${table_id}`;
  return makeAuthenticatedRequest(url, {
    method: "PUT",
    body: JSON.stringify({ preferences }),
  });
};

// Replace the user's favorite-workflow list. The list is per-user (the
// backend keys it by the verified token uid), so there's no id_client here.
export const saveFavoriteWorkflows = async (
  workflow_ids: FavoriteWorkflowsBody["workflow_ids"]
): Promise<Record<string, unknown>> => {
  const url = `${getApiUrl()}/user_preferences/favorites`;
  return makeAuthenticatedRequest(url, {
    method: "PUT",
    body: JSON.stringify({ workflow_ids }),
  });
};

// Replace the user's workspace-switcher visibility pref (focus mode). Per-user
// (backend keys it by uid). UI-only — never affects access.
export const saveSwitcherVisibility = async ({
  switcher_focus_mode,
}: Pick<SwitcherVisibilityBody, "switcher_focus_mode">): Promise<
  Record<string, unknown>
> => {
  const url = `${getApiUrl()}/user_preferences/switcher_visibility`;
  return makeAuthenticatedRequest(url, {
    method: "PUT",
    body: JSON.stringify({ switcher_focus_mode }),
  });
};

// Replace the user's persona config for ONE workspace. The workspace rides in
// the body, so the path stays a single segment and cannot be captured by the
// two-segment column-prefs route. The persona narrows what the interface
// offers and grants nothing; entitlement is untouched.
export const savePersonaConfig = async (
  config: PersonaConfigBody
): Promise<Record<string, unknown>> => {
  const url = `${getApiUrl()}/user_preferences/persona_config`;
  return makeAuthenticatedRequest(url, {
    method: "PUT",
    body: JSON.stringify(config),
  });
};
